export interface PlaylistItem {
  slideIndex: number;
  frameIndex: number;
  durationMs: number;
}

const toMs = (seconds: number) => Math.max(1, seconds) * 1000;

const buildSlideItems = ({
  slide,
  slideIndex,
  intervalMs,
  intervalTimeSeconds,
}: {
  slide: WallboardScreenshots.Slide;
  slideIndex: number;
  intervalMs: number;
  intervalTimeSeconds: number;
}): PlaylistItem[] => {
  if (slide.kind === 'weather') {
    return [{ slideIndex, frameIndex: 0, durationMs: intervalMs }];
  }
  // A frame that failed with no earlier screenshot to fall back on has no
  // path: skip it (the builder reports it).
  const playable = slide.frames
    .map((frame, frameIndex) => ({ frame, frameIndex }))
    .filter(({ frame }) => !!frame.path);
  if (playable.length === 0) {
    return [];
  }
  if (playable.length === 1) {
    return [
      {
        slideIndex,
        frameIndex: playable[0].frameIndex,
        durationMs: intervalMs,
      },
    ];
  }

  const pass = playable.map(({ frame, frameIndex }) => ({
    slideIndex,
    frameIndex,
    durationMs: toMs(frame.durationSeconds ?? intervalTimeSeconds),
  }));
  const passMs = pass.reduce((total, item) => total + item.durationMs, 0);
  const passCount = Math.max(1, Math.ceil(intervalMs / passMs));
  return Array.from({ length: passCount }, () => pass).reduce<PlaylistItem[]>(
    (items, repeat) => items.concat(repeat),
    [],
  );
};

/**
 * The player's rotation. Each slide shows for the wallboard's interval, as the
 * live wallboard does. A driver leaderboard has a frame per rotation step: each
 * plays for its own dwell, at least once through, repeating until the slide's
 * interval is used up (the live leaderboard rotates inside its slide).
 *
 * The standalone TV player (src/tvPlayer) runs this on browsers as old as
 * Chrome 53, so it avoids flatMap/flat.
 */
const buildPlaylist = ({
  manifest,
}: {
  manifest: WallboardScreenshots.Manifest;
}): PlaylistItem[] => {
  const intervalMs = toMs(manifest.intervalTimeSeconds);

  return manifest.slides.reduce<PlaylistItem[]>(
    (playlist, slide, slideIndex) =>
      playlist.concat(
        buildSlideItems({
          slide,
          slideIndex,
          intervalMs,
          intervalTimeSeconds: manifest.intervalTimeSeconds,
        }),
      ),
    [],
  );
};

export default buildPlaylist;
